import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Treemap, ResponsiveContainer } from "recharts";
import { Info, TrendingUp, TrendingDown } from "lucide-react";

// ─── Carat display groups — fold fine-grained API bins into display labels ─────
const CARAT_GROUPS = [
  { label: "0.5-1ct",   ranges: ["0.50-0.59","0.60-0.69","0.70-0.79","0.80-0.89","0.90-0.99"] },
  { label: "1-1.49ct",  ranges: ["1.00-1.19","1.20-1.49"] },
  { label: "1.5-2ct",   ranges: ["1.50-1.69","1.70-1.99"] },
  { label: "2-2.99ct",  ranges: ["2.00-2.19","2.20-2.49","2.50-2.99"] },
  { label: "3-4ct",     ranges: ["3.00-3.49","3.50-3.99"] },
  { label: "4+ct",      ranges: ["4.00-4.99","5.00-5.99","6.00-6.99","7.00-7.99","8.00-8.99","9.00-9.99","10.00-99.99"] },
];

// API values can be { source, parsedValue } objects or plain numbers
function parseVal(v) {
  if (v == null) return 0;
  if (typeof v === "object" && v.parsedValue != null) return Number(v.parsedValue) || 0;
  return Number(v) || 0;
}

// Build the flat treemap items from shape_size_grid
function processShapeSizeGrid(grid) {
  if (!grid || typeof grid !== "object") return [];
  const tiles = [];
  for (const shapeData of Object.values(grid)) {
    const shapeName = shapeData?.trans;
    if (!shapeName) continue;
    for (const group of CARAT_GROUPS) {
      let totalInvValue = 0;
      let weightedTurn  = 0;
      let totalInventory = 0;
      for (const range of group.ranges) {
        const bin = shapeData[range];
        if (!bin) continue;
        const iv  = parseVal(bin.inv_value);
        const t   = parseVal(bin.turn);
        const inv = parseVal(bin.inventory);
        if (iv > 0) {
          totalInvValue += iv;
          weightedTurn  += iv * t;
        }
        totalInventory += inv;
      }
      if (totalInvValue <= 0) continue;
      tiles.push({
        name:      `${shapeName} ${group.label}`,
        shape:     shapeName,
        size:      totalInvValue,
        inv_value: totalInvValue,
        turn:      weightedTurn / totalInvValue,
        inventory: totalInventory,
      });
    }
  }
  return tiles;
}

// ─── Turn-rate colour tiers ────────────────────────────────────────────────────
const TURN_TIERS = [
  { label: "Excellent",  min: 2.5, suffix: "≥2.5", color: "#059669", bg: "#d1fae5", textColor: "#065f46" },
  { label: "Very Good",  min: 2.0, suffix: "≥2",   color: "#10B981", bg: "#a7f3d0", textColor: "#047857" },
  { label: "Good",       min: 1.5, suffix: "≥1.5", color: "#84CC16", bg: "#f7fee7", textColor: "#3f6212" },
  { label: "Fair",       min: 1.0, suffix: "≥1",   color: "#EAB308", bg: "#fef9c3", textColor: "#713f12" },
  { label: "Poor",       min: 0.5, suffix: "≥0.5", color: "#F97316", bg: "#ffedd5", textColor: "#7c2d12" },
  { label: "Very Poor",  min: 0,   suffix: "<0.5",  color: "#DC2626", bg: "#fee2e2", textColor: "#7f1d1d" },
];

function getTier(turn) {
  for (const tier of TURN_TIERS) {
    if (turn >= tier.min) return tier;
  }
  return TURN_TIERS[TURN_TIERS.length - 1];
}

function fmtM(val) {
  if (!val) return "$0";
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000)     return `$${(val / 1_000).toFixed(1)}K`;
  return `$${Math.round(val).toLocaleString()}`;
}

// Shared SVG text props — avoids repeating pointer-events + select on every element
const T = { style: { pointerEvents: "none", userSelect: "none" } };

// ─── SVG tile renderer factory ────────────────────────────────────────────────
function makeTileRenderer(onEnter, onLeave, onMove) {
  return function TileContent(props) {
    const { x, y, width, height, name, inv_value, turn, inventory, shape, depth } = props;

    // Guard: recharts calls content for the synthetic root node (depth=0, no item fields)
    // and for tiles too small to label.
    if (!name || depth === 0 || width < 4 || height < 4) return null;

    const tier    = getTier(turn || 0);
    const cx      = x + width  / 2;
    const cy      = y + height / 2;
    const carat   = shape ? name.slice(shape.length + 1) : name;
    const turnTxt = `Turn: ${Number(turn || 0).toFixed(1)}`;
    const valTxt  = fmtM(inv_value);

    // Display tiers — determined by tile pixel size
    const showFull = width > 130 && height > 68;
    const showMed4 = width > 55  && height > 56;
    const showMed2 = width > 42  && height > 30;
    const showTiny = width > 26  && height > 16;

    return (
      <g
        onMouseEnter={(e) => onEnter(e, { name, inv_value, turn, inventory })}
        onMouseLeave={onLeave}
        onMouseMove={onMove}
        style={{ cursor: "default" }}
      >
        <rect x={x} y={y} width={width} height={height} fill={tier.color} stroke="#fff" strokeWidth={2} />

        {/* Large: full label + value + turn (3 rows) */}
        {showFull && (
          <>
            <text x={cx} y={cy - 13} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={13} {...T}>{name}</text>
            <text x={cx} y={cy +  3} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.9)"  fontSize={11} {...T}>{valTxt}</text>
            <text x={cx} y={cy + 17} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.75)" fontSize={10} {...T}>{turnTxt}</text>
          </>
        )}

        {/* Med-4: shape / carat / value / turn (4 rows) */}
        {!showFull && showMed4 && (
          <>
            <text x={cx} y={cy - 18} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={11} {...T}>{shape}</text>
            <text x={cx} y={cy -  6} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.9)"  fontSize={10} {...T}>{carat}</text>
            <text x={cx} y={cy +  6} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.85)" fontSize={10} {...T}>{valTxt}</text>
            <text x={cx} y={cy + 18} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.75)" fontSize={9}  {...T}>{turnTxt}</text>
          </>
        )}

        {/* Med-2: shape / carat (2 rows) */}
        {!showFull && !showMed4 && showMed2 && (
          <>
            <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={Math.min(11, Math.floor(width / 5.5))} {...T}>{shape}</text>
            <text x={cx} y={cy + 6} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.85)" fontSize={Math.min(10, Math.floor(width / 6.5))} {...T}>{carat}</text>
          </>
        )}

        {/* Tiny: shape only */}
        {!showFull && !showMed4 && !showMed2 && showTiny && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={Math.min(10, Math.floor(width / 4))} {...T}>{shape}</text>
        )}
      </g>
    );
  };
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function InventoryTreemap({ shapeSizeGrid }) {
  const [tooltip, setTooltip] = useState(null);

  // Entrance animation — mount the Treemap only when it scrolls into view so
  // recharts plays its tile-grow animation at the right moment.
  const treemapRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = treemapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleEnter = useCallback((e, item) => {
    setTooltip({ x: e.clientX, y: e.clientY, ...item });
  }, []);
  const handleLeave = useCallback(() => setTooltip(null), []);
  // onMove only updates position — avoids re-allocating the item object every pixel
  const handleMove = useCallback((e) => {
    setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  }, []);

  const tileRenderer = useMemo(
    () => makeTileRenderer(handleEnter, handleLeave, handleMove),
    [handleEnter, handleLeave, handleMove],
  );

  const tiles = useMemo(() => processShapeSizeGrid(shapeSizeGrid), [shapeSizeGrid]);

  const totalInv = useMemo(() => tiles.reduce((s, d) => s + d.inv_value, 0), [tiles]);
  const avgTurn  = useMemo(() => {
    const w = tiles.reduce((s, d) => s + d.turn * d.inv_value, 0);
    return totalInv > 0 ? w / totalInv : 0;
  }, [tiles, totalInv]);

  const high   = useMemo(() => tiles.filter(d => d.turn >= 2.0).length, [tiles]);
  const medium = useMemo(() => tiles.filter(d => d.turn >= 1.0 && d.turn < 2.0).length, [tiles]);
  const low    = useMemo(() => tiles.filter(d => d.turn < 1.0).length, [tiles]);

  const hasData = tiles.length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
      {/* ── Header ── */}
      <div className="flex items-start justify-between pb-3 border-b border-gray-100 mb-4">
        <div>
          <h2 className="text-[17px] font-semibold text-gray-900">
            Inventory Breakdown by Category
          </h2>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Size represents inventory value, color indicates turnover rate
          </p>
        </div>
        {hasData && (
          <div className="text-right flex-shrink-0">
            <p className="text-[11px] text-gray-400 font-medium">Total Inventory</p>
            <p className="text-[24px] font-extrabold text-gray-900 leading-none mt-0.5">
              {fmtM(totalInv)}
            </p>
            <p className="text-[12px] text-gray-500 mt-1">
              Avg Turn:{" "}
              <span className="font-semibold text-gray-700">{avgTurn.toFixed(2)}</span>
            </p>
          </div>
        )}
      </div>

      {/* ── Empty / loading state ── */}
      {!hasData && (
        <div className="h-[460px] flex items-center justify-center text-gray-300 text-xs border border-dashed border-gray-200 rounded-lg">
          No inventory data available
        </div>
      )}

      {/* ── Treemap + legend + summary cards ── */}
      {hasData && (
        <>
          {/* Container keeps its height before inView so layout doesn't jump;
              fade + slide-up entrance fires once when scrolled into view. */}
          <div
            ref={treemapRef}
            style={{
              height: 460,
              position: "relative",
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.55s ease-out, transform 0.55s ease-out",
            }}
          >
            {inView && (
              <ResponsiveContainer width="100%" height="100%">
                <Treemap
                  data={tiles}
                  dataKey="size"
                  aspectRatio={4 / 3}
                  content={tileRenderer}
                  isAnimationActive
                  animationDuration={500}
                />
              </ResponsiveContainer>
            )}

            {/* Floating tooltip — fixed so it escapes SVG stacking context */}
            {tooltip && inView && (
              <div
                style={{
                  position: "fixed",
                  left: tooltip.x + 14,
                  top: Math.max(8, tooltip.y - 100),
                  zIndex: 9999,
                  pointerEvents: "none",
                }}
                className="bg-white border border-gray-200 rounded-xl shadow-xl px-3 py-2.5 min-w-[190px]"
              >
                <p className="font-bold text-gray-900 text-[13px] mb-2">{tooltip.name}</p>
                <div className="flex items-center justify-between gap-4 mb-1">
                  <span className="text-[12px] text-gray-500">Inventory Value:</span>
                  <span className="text-[12px] font-bold text-gray-800">{fmtM(tooltip.inv_value)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 mb-1">
                  <span className="text-[12px] text-gray-500">Turnover (12M):</span>
                  <span className="text-[12px] font-bold text-gray-800">
                    {Number(tooltip.turn || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[12px] text-gray-500">Performance:</span>
                  {(() => {
                    const tier = getTier(tooltip.turn || 0);
                    return (
                      <span
                        className="text-[11px] font-bold px-2 py-0.5 rounded-md"
                        style={{ backgroundColor: tier.bg, color: tier.textColor }}
                      >
                        {tier.label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* ── Legend ── */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 mb-2">
              <Info className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[12px] font-semibold text-gray-500">Turnover Rate Legend</span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              {TURN_TIERS.map((tier) => (
                <div key={tier.label} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: tier.color }} />
                  <span className="text-[12px] font-semibold text-gray-700">{tier.label}</span>
                  <span className="text-[11px] text-gray-400">{tier.suffix}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Summary cards ── */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                <span className="text-[12px] font-bold text-emerald-700">High Performers</span>
              </div>
              <p className="text-[11px] text-emerald-600 font-medium">
                {high} {high === 1 ? "category" : "categories"} with turnover ≥ 2.0
              </p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Info className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <span className="text-[12px] font-bold text-amber-700">Medium Performers</span>
              </div>
              <p className="text-[11px] text-amber-600 font-medium">
                {medium} {medium === 1 ? "category" : "categories"} between 1.0–2.0
              </p>
            </div>
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingDown className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                <span className="text-[12px] font-bold text-red-700">Need Attention</span>
              </div>
              <p className="text-[11px] text-red-600 font-medium">
                {low} {low === 1 ? "category" : "categories"} with turnover &lt; 1.0
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
